import {
  Box,
  Center,
  Divider,
  Stack,
  Text,
  Transition,
  UnstyledButton,
} from "@mantine/core";
import { Routing } from "config/enums/routings";
import { ROUTING_ICONS } from "config/icons";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import "./styles.css";
import { createStyles } from "@mantine/emotion";

const getRoutingItems = (t: TFunction) => [
  {
    icon: ROUTING_ICONS.bicycle,
    label: t("general.routing.bike"),
    value: Routing.bicycle,
  },
  {
    icon: ROUTING_ICONS.auto,
    label: t("general.routing.car"),
    value: Routing.auto,
  },
  {
    icon: ROUTING_ICONS.pedestrian,
    label: t("general.routing.walk"),
    value: Routing.pedestrian,
  },
  {
    icon: ROUTING_ICONS.none,
    label: t("general.routing.none"),
    value: Routing.none,
  },
];

const scaleTopLeft = {
  in: { opacity: 1, transform: "scale(1)" },
  out: { opacity: 0, transform: "scale(0)" },
  common: { transformOrigin: "top left" },
  transitionProperty: "transform, opacity",
};

const useStyles = createStyles(
  (_, { left, top }: { left: number; top: number }, u) => ({
    container: {
      [u.light]: {
        backgroundColor: "#fff",
        border: "1px solid #ced4da",
      },
      [u.dark]: {
        backgroundColor: "#1A1B1E",
        border: "none",
      },
      left,
      top,
    },
  })
);

export const MainMenu = ({
  bicycleIcon,
  position,
  /* The value prop should be used only to set a visual cue if the sub menu is open */
  value: focusedItemValue,
  visible = false,
  onItemClick,
  onItemMouseEnter,
}: {
  bicycleIcon?: JSX.Element;
  position: { left: number; top: number };
  value?: Routing;
  visible: boolean;
  onItemClick: (value: Routing) => void;
  onItemMouseEnter: (value: Routing) => void;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles(position);

  return (
    <Box className={`trkbk-context-menu main-menu ${classes.container}`}>
      <Transition
        mounted={visible}
        transition={scaleTopLeft}
        duration={150}
        timingFunction="ease"
      >
        {(styles) => (
          <div style={styles} onContextMenu={(e) => e.preventDefault()}>
            <Text size="xs" px="xs" py={4} color="dimmed">
              {t("route_to_point_context_menu.title")}
            </Text>
            <Divider />
            <Stack gap={0} py={4}>
              {getRoutingItems(t).map(({ icon, label, value }) => (
                <UnstyledButton
                  key={value}
                  className="trkbk-context-menu main-menu-item"
                  px="xs"
                  sx={(theme, u) => ({
                    [u.light]: {
                      backgroundColor:
                        focusedItemValue === value
                          ? theme.colors.gray[1]
                          : "inherit",
                    },
                    [u.dark]: {
                      backgroundColor:
                        focusedItemValue === value
                          ? theme.colors.dark[4]
                          : "inherit",
                    },
                    "&:hover": {
                      [u.light]: {
                        backgroundColor: theme.colors.gray[1],
                      },
                      [u.dark]: {
                        backgroundColor: theme.colors.dark[4],
                      },
                    },
                  })}
                  onClick={() => onItemClick(value)}
                  onMouseEnter={() => onItemMouseEnter(value)}
                >
                  <Center
                    className="icon"
                    sx={(theme, u) => ({
                      [u.light]: {
                        color: theme.colors.gray[7],
                      },
                      [u.dark]: {
                        color: theme.colors.dark[1],
                      },
                    })}
                  >
                    {bicycleIcon && value === Routing.bicycle
                      ? bicycleIcon
                      : icon}
                  </Center>
                  <Text
                    className="text"
                    py={8}
                    sx={(_, u) => ({
                      [u.light]: {
                        color: "#495057",
                      },
                      [u.dark]: {
                        color: "inherit",
                      },
                    })}
                  >
                    {label}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          </div>
        )}
      </Transition>
    </Box>
  );
};
